import React from 'react'
import PropTypes from 'prop-types'
import { useOverlay } from '@fs/zion-ui'
import AddGroupMembersMenu from './AddGroupMembersMenu'
import AddExistingPersonFlow from '../../panels/addExistingPersonPanel/AddExistingPersonFlow'
import { ADD_EXISTING_PERSON_ENTRY_POINTS } from '../../panels/addExistingPersonPanel/utils/addExistingPersonConstants'

/**
 * The "Add group members" menu with its "Add existing person" destination supplied.
 *
 * The sheet is a sibling of the menu rather than something the menu renders: it has to stay up after
 * the menu item that opened it is gone, and again behind each confirmation step.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.person Focus person as an SLS person; never a selectable row in the sheet.
 * @param {string} [props.personName] Viewed person's full name, for the menu's accessible name.
 * @param {Array<string>} [props.pendingMemberIds] The subset of `groupMemberIds` staged but not yet saved.
 * @param {Array<string>} [props.groupMemberIds] Person ids already in the group, taken from the card's
 *   form state so names added but not yet saved cannot be offered again.
 * @param {Function} [props.onAddExistingPerson] Caller-owned destination; takes precedence over the sheet.
 * @param {Function} [props.onAddNewPerson] Handler for "Add new person".
 * @param {(result: import('../../panels/addExistingPersonPanel/useAddExistingPersonFlow').AddExistingPersonResult) => (void|Promise<void>)}
 *   [props.onSave] Called with everything the patron agreed to. A returned promise is awaited before
 *   either surface comes down.
 * @returns {JSX.Element}
 */
const AddGroupMembersFlow = ({
  person,
  personName,
  groupMemberIds,
  pendingMemberIds,
  onAddExistingPerson,
  onAddNewPerson,
  onSave,
}) => {
  const addExistingPersonPanelOverlay = useOverlay()

  return (
    <>
      <AddGroupMembersMenu
        personName={personName}
        onAddExistingPerson={onAddExistingPerson ?? addExistingPersonPanelOverlay.handleClick}
        onAddNewPerson={onAddNewPerson}
      />
      <AddExistingPersonFlow
        overlay={addExistingPersonPanelOverlay}
        entryPoint={ADD_EXISTING_PERSON_ENTRY_POINTS.RELATIONSHIPS_CARD}
        focusPersonName={personName}
        focusPersonId={person?.id}
        groupMemberIds={groupMemberIds}
        pendingMemberIds={pendingMemberIds}
        onSave={onSave}
      />
    </>
  )
}

AddGroupMembersFlow.propTypes = {
  person: PropTypes.shape({
    id: PropTypes.string,
  }),
  personName: PropTypes.string,
  groupMemberIds: PropTypes.arrayOf(PropTypes.string),
  pendingMemberIds: PropTypes.arrayOf(PropTypes.string),
  onAddExistingPerson: PropTypes.func,
  onAddNewPerson: PropTypes.func,
  onSave: PropTypes.func,
}

AddGroupMembersFlow.defaultProps = {
  person: undefined,
  personName: '',
  groupMemberIds: [],
  pendingMemberIds: [],
  onAddExistingPerson: undefined,
  onAddNewPerson: undefined,
  onSave: () => {},
}

export default AddGroupMembersFlow
